// Pure, DOM-free transforms that insert/remove markdown syntax in a piece of
// text given a selection range. Used by the Textarea markdown toolbar. Kept
// fully testable (no React, no DOM) — see markdown.test.ts.

export interface MarkSelection {
  value: string
  selectionStart: number
  selectionEnd: number
}

export type InlineMark = 'bold' | 'italic'
export type BlockMark = 'heading' | 'bullet' | 'numbered'

export const INLINE_MARKERS: Record<InlineMark, string> = {
  bold: '**',
  italic: '_',
}

const BLOCK_PREFIXES: Record<BlockMark, string> = {
  heading: '# ',
  bullet: '- ',
  numbered: '1. ',
}

// Matches the leading prefix of any known block mark on a single line.
const ANY_BLOCK_PREFIX = /^(?:# |- |\d+\. )/
const BLOCK_PREFIX_TESTS: Record<BlockMark, RegExp> = {
  heading: /^# /,
  bullet: /^- /,
  numbered: /^\d+\. /,
}

const stripAnyBlockPrefix = (line: string) => line.replace(ANY_BLOCK_PREFIX, '')

// Expands a selection to cover the whole line(s) it touches.
const expandToLines = (sel: MarkSelection) => {
  const lineStart = sel.value.lastIndexOf('\n', sel.selectionStart - 1) + 1
  let lineEnd = sel.value.indexOf('\n', sel.selectionEnd)
  if (lineEnd === -1) lineEnd = sel.value.length
  return { lineStart, lineEnd, block: sel.value.slice(lineStart, lineEnd) }
}

export const toggleInlineMark = (
  sel: MarkSelection,
  mark: InlineMark
): MarkSelection => {
  const marker = INLINE_MARKERS[mark]
  const len = marker.length
  const { value, selectionStart: start, selectionEnd: end } = sel
  const selected = value.slice(start, end)

  // Empty selection → insert an empty marker pair, place caret in between.
  if (start === end) {
    return {
      value: value.slice(0, start) + marker + marker + value.slice(end),
      selectionStart: start + len,
      selectionEnd: start + len,
    }
  }

  // Markers sit immediately outside the selection → unwrap.
  if (
    value.slice(start - len, start) === marker &&
    value.slice(end, end + len) === marker
  ) {
    return {
      value: value.slice(0, start - len) + selected + value.slice(end + len),
      selectionStart: start - len,
      selectionEnd: end - len,
    }
  }

  // Markers are part of the selection → unwrap.
  if (
    selected.length >= 2 * len &&
    selected.slice(0, len) === marker &&
    selected.slice(selected.length - len) === marker
  ) {
    const inner = selected.slice(len, selected.length - len)
    return {
      value: value.slice(0, start) + inner + value.slice(end),
      selectionStart: start,
      selectionEnd: start + inner.length,
    }
  }

  // Default → wrap the selection, keep the inner text selected.
  return {
    value:
      value.slice(0, start) + marker + selected + marker + value.slice(end),
    selectionStart: start + len,
    selectionEnd: end + len,
  }
}

export const isInlineMarkActive = (
  sel: MarkSelection,
  mark: InlineMark
): boolean => {
  const marker = INLINE_MARKERS[mark]
  const len = marker.length
  const { value, selectionStart: start, selectionEnd: end } = sel
  const selected = value.slice(start, end)

  if (
    value.slice(start - len, start) === marker &&
    value.slice(end, end + len) === marker
  ) {
    return true
  }
  return (
    selected.length >= 2 * len &&
    selected.slice(0, len) === marker &&
    selected.slice(selected.length - len) === marker
  )
}

export const toggleBlockMark = (
  sel: MarkSelection,
  mark: BlockMark
): MarkSelection => {
  const { lineStart, lineEnd, block } = expandToLines(sel)
  const lines = block.split('\n')
  const single = lines.length === 1
  const test = BLOCK_PREFIX_TESTS[mark]

  const nonEmpty = lines.filter((line) => line.trim().length > 0)
  const active =
    nonEmpty.length > 0 && nonEmpty.every((line) => test.test(line))

  let newLines: string[]
  if (active) {
    // Toggle off → strip this mark's prefix from each line.
    newLines = lines.map((line) => line.replace(test, ''))
  } else if (mark === 'numbered') {
    let n = 0
    newLines = lines.map((line) => {
      const base = stripAnyBlockPrefix(line)
      if (base.trim().length === 0 && !single) return base
      n += 1
      return `${n}. ${base}`
    })
  } else {
    newLines = lines.map((line) => {
      const base = stripAnyBlockPrefix(line)
      if (base.trim().length === 0 && !single) return base
      return BLOCK_PREFIXES[mark] + base
    })
  }

  const newBlock = newLines.join('\n')
  return {
    value: sel.value.slice(0, lineStart) + newBlock + sel.value.slice(lineEnd),
    selectionStart: lineStart,
    selectionEnd: lineStart + newBlock.length,
  }
}

export const isBlockMarkActive = (
  sel: MarkSelection,
  mark: BlockMark
): boolean => {
  const { block } = expandToLines(sel)
  const test = BLOCK_PREFIX_TESTS[mark]
  const nonEmpty = block.split('\n').filter((line) => line.trim().length > 0)
  return nonEmpty.length > 0 && nonEmpty.every((line) => test.test(line))
}

// Any list item marker, and the indented continuation of one.
const LIST_ITEM = /^\s*(?:[-*+]|\d+[.)])\s/
const INDENTED = /^\s{2,}\S/

export interface INormalizedMarkdown {
  markdown: string
  // 1-based line numbers in `markdown` where a block starts that the writer
  // separated with a single Enter instead of an empty line. Such a block is
  // rendered without the gap above it, so the reader sees the spacing the
  // editor showed while typing.
  tightLines: Set<number>
}

/**
 * Makes the rendered markdown follow the Enters the writer actually typed.
 *
 * CommonMark folds a plain line that directly follows a list item into that
 * item ("lazy continuation"), which silently eats the newline — so the blank
 * line the writer meant is inserted here. That blank line would otherwise buy
 * a full empty line of spacing they never asked for, hence `tightLines`: every
 * block that started on a single Enter is flagged so the styling can close the
 * gap again.
 */
export const normalizeMarkdown = (markdown: string): INormalizedMarkdown => {
  const tightLines = new Set<number>()
  const lines: string[] = []
  let inList = false
  let previous: string | undefined

  markdown.split('\n').forEach((line) => {
    const breakOut =
      inList &&
      line.trim() !== '' &&
      !LIST_ITEM.test(line) &&
      !INDENTED.test(line)
    inList = LIST_ITEM.test(line) || (inList && INDENTED.test(line))

    if (breakOut) lines.push('')
    lines.push(line)
    if (previous?.trim() && line.trim()) tightLines.add(lines.length)
    previous = line
  })

  return { markdown: lines.join('\n'), tightLines }
}
